// src/components/personal/sections/ClientsSection/components/ClientCard/ClientOrders.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatSmartDate } from '@/utils/Formatters';
import { Table, TableColumn } from '@/components/ui/Table/Table';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';
import { useColumnWidths } from '@/hooks/useColumnWidths';
import { RefreshCw } from 'lucide-react';

interface OrderType {
  id: string;
  date: string;
  status: string;
  total: number;
}

interface ClientOrdersProps {
  orders: OrderType[];
}

// Вспомогательная функция для получения класса стиля статуса заказа
const getStatusClassName = (status: string): string => {
  const statusMap: Record<string, string> = {
    'new': 'text-blue-600 bg-blue-100',
    'processing': 'text-yellow-600 bg-yellow-100',
    'completed': 'text-green-600 bg-green-100',
    'canceled': 'text-red-600 bg-red-100',
    'abandoned': 'text-gray-600 bg-gray-100',
    'delivered': 'text-teal-600 bg-teal-100'
  };
  
  return statusMap[status] || 'text-gray-600 bg-gray-100';
};

const ClientOrders: React.FC<ClientOrdersProps> = ({ orders }) => {
  const { t } = useTranslation(['clients', 'common']);
  const { formatPrice } = useBusinessCurrency();
  
  // Используем хук для сохранения ширины столбцов
  const [columnWidths, setColumnWidths] = useColumnWidths('client-orders-table');
  
  // Определяем колонки для таблицы заказов
  const columns: TableColumn<OrderType>[] = [
    {
      key: 'id',
      title: t('orders.table.id'),
      width: 'sm',
      resizable: true,
      render: (order) => (
        <span className="font-medium">{order.id}</span>
      ),
      mobilePriority: 0 // Самый высокий приоритет для мобильных
    },
    {
      key: 'date',
      title: t('orders.table.date'),
      width: 'md',
      resizable: true,
      render: (order) => {
        return order.date ? formatSmartDate(order.date, true, t('common:locale')) : '—';
      },
      mobilePriority: 1
    },
    {
      key: 'status',
      title: t('orders.table.status'),
      width: 'lg',
      resizable: true,
      render: (order) => (
        <span className={`px-2 py-1 rounded text-xs ${getStatusClassName(order.status)}`}>
          {t(`orders.statuses.${order.status}`) || order.status}
        </span>
      ),
      mobilePriority: 2
    },
    {
      key: 'total',
      title: t('orders.table.total'),
      width: 'sm',
      resizable: true,
      textAlign: 'right',
      render: (order) => (
        <span className="font-medium">
          {formatPrice(order.total)}
        </span>
      ),
      mobilePriority: 1
    }
  ];

  if (!orders || orders.length === 0) {
    return (
      <div className="text-center py-8 text-secondary">
        {t('orders.no_orders')}
      </div>
    );
  }

  // Вычисляем общую сумму всех заказов
  const totalAmount = orders.reduce((sum, order) => sum + order.total, 0);

  // Функция для сброса ширины столбцов к значениям по умолчанию
  const resetColumnWidths = () => {
    setColumnWidths({});
  };

  return (
    <div className="space-y-4">
      {/* Кнопка для сброса настроек столбцов */}
      {Object.keys(columnWidths).length > 0 && (
        <div className="flex justify-end mb-2">
          <button 
            onClick={resetColumnWidths}
            className="text-xs text-gray-500 hover:text-primary transition-colors flex items-center"
            title={t('common:reset_to_default')}
          >
            <RefreshCw className="w-3 h-3 mr-1" />
            {t('common:reset_columns')}
          </button>
        </div>
      )}

      <Table
        data={orders}
        columns={columns}
        emptyText={t('orders.no_orders')}
        scrollable={false}
        resizableColumns={true}
        savedColumnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
        // Добавляем адаптивность
        responsive={true}
        mobileBehavior="cards"
      />

      <div className="flex justify-between items-center bg-background p-3 rounded-lg">
        <div>
          <span className="text-secondary">
            {t('orders.total_orders', { count: orders.length })}
          </span>
        </div>
        <div>
          <span className="font-medium mr-2">
            {t('orders.table.grand_total')}:
          </span>
          <span className="font-medium text-primary">
            {formatPrice(totalAmount)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ClientOrders;